/**
 * Bedridden Patients List Page
 *
 * CRUD: List with pagination, search, filters
 */

import { useState } from 'react'
import { BedSingle, Plus, Edit, Trash2, Search } from 'lucide-react'
import { Sidebar, PageHeader, LoadingSpinner } from '../shared/components'
import { useBedriddenPatientsList, useDiseases, deleteBedriddenPatient } from '../services/api'
import type { BedriddenPatient } from '../services/api'
import { useDebounce } from '../hooks/useDebounce'

export function BedriddenPatientsList() {
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [typeFilter, setTypeFilter] = useState<'home' | 'bed' | ''>('')
  const [severityFilter, setSeverityFilter] = useState<'high' | 'medium' | 'low' | ''>('')
  const [groupFilter, setGroupFilter] = useState<number | null>(null)
  const [diseaseFilter, setDiseaseFilter] = useState<number | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Debounce search input to prevent API call on every keystroke
  const debouncedSearch = useDebounce(searchInput, 300)

  // Fetch diseases for filter dropdown
  const { diseases } = useDiseases()

  const { patients, pagination, isLoading, error, mutate } = useBedriddenPatientsList({
    page,
    per_page: 20,
    search: debouncedSearch || undefined,
    type: typeFilter || undefined,
    severity: severityFilter || undefined,
    vulnerable_group_id: groupFilter || undefined,
    disease_id: diseaseFilter || undefined,
  })

  const handleDelete = async (patientId: number) => {
    if (!confirm('ต้องการลบผู้ป่วยนี้หรือไม่?')) {
      return
    }

    try {
      setDeleteError(null)
      await deleteBedriddenPatient(patientId)
      // Refresh list after successful delete
      mutate()
    } catch (err: any) {
      console.error('Delete error:', err)
      setDeleteError(err.message || 'ไม่สามารถลบข้อมูลได้')
    }
  }

  const getTypeLabel = (type: 'home' | 'bed') => {
    return type === 'home' ? 'ติดบ้าน' : 'ติดเตียง'
  }

  const getSeverityLabel = (severity: 'high' | 'medium' | 'low') => {
    const labels = { high: 'สูง', medium: 'กลาง', low: 'ต่ำ' }
    return labels[severity]
  }

  const getSeverityColor = (severity: 'high' | 'medium' | 'low') => {
    const colors = {
      high: 'bg-red-100 text-red-700',
      medium: 'bg-orange-100 text-orange-700',
      low: 'bg-green-100 text-green-700',
    }
    return colors[severity]
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar currentPage="bedridden" />

      <main className="flex-1 overflow-auto">
        <PageHeader
          title="รายชื่อผู้ป่วยติดบ้าน/ติดเตียง"
          subtitle="จัดการข้อมูลผู้ป่วยรายบุคคล"
          icon={BedSingle}
        />

        <div className="p-6">
          {/* Actions Bar */}
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="ค้นหา..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-700"
              />
            </div>

            {/* Add Button */}
            <button
              onClick={() => (window.location.hash = 'bedridden-patients-new')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-korat-navy-700 text-white rounded-lg hover:bg-korat-navy-800 transition-colors"
            >
              <Plus className="w-5 h-5" />
              เพิ่มผู้ป่วยใหม่
            </button>
          </div>

          {/* Filters */}
          <div className="mb-6 flex gap-3">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as any)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-700"
            >
              <option value="">ทุกประเภท</option>
              <option value="home">ติดบ้าน</option>
              <option value="bed">ติดเตียง</option>
            </select>

            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value as any)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-700"
            >
              <option value="">ทุกระดับความเสี่ยง</option>
              <option value="high">สูง</option>
              <option value="medium">กลาง</option>
              <option value="low">ต่ำ</option>
            </select>

            <select
              value={groupFilter || ''}
              onChange={(e) => setGroupFilter(e.target.value ? parseInt(e.target.value) : null)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-700"
            >
              <option value="">ทุกกลุ่ม</option>
              <option value="5">ติดบ้าน/ติดเตียง</option>
              <option value="1">ผู้สูงอายุ</option>
              <option value="3">เสี่ยงสูง</option>
            </select>

            <select
              value={diseaseFilter || ''}
              onChange={(e) => setDiseaseFilter(e.target.value ? parseInt(e.target.value) : null)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-korat-navy-700"
            >
              <option value="">ทุกโรค</option>
              {diseases.map((disease) => (
                <option key={disease.id} value={disease.id}>
                  {disease.name_th}
                </option>
              ))}
            </select>
          </div>

          {/* Total Count */}
          {!isLoading && !error && pagination && (
            <div className="mb-4 text-sm text-slate-600">
              พบผู้ป่วยทั้งหมด{' '}
              <span className="font-semibold text-korat-navy-700">
                {pagination.total.toLocaleString('th-TH')}
              </span>{' '}
              รายการ
              {debouncedSearch && (
                <span className="ml-2 text-slate-500">
                  (กรองจาก "{debouncedSearch}")
                </span>
              )}
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              ⚠️ ไม่สามารถโหลดข้อมูลได้: {error.message}
            </div>
          )}

          {/* Delete Error */}
          {deleteError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              ⚠️ {deleteError}
            </div>
          )}

          {/* Table */}
          {!isLoading && !error && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">ID</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">อำเภอ/ตำบล</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">กลุ่ม</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">ประเภท</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">ความเสี่ยง</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">โรคเรื้อรัง</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {patients.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                          ไม่พบข้อมูลผู้ป่วย
                        </td>
                      </tr>
                    ) : (
                      patients.map((patient) => (
                        <tr key={patient.id} className="hover:bg-slate-50 focus-within:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-slate-700">
                            #{patient.id}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-slate-700">{patient.district_name}</div>
                            <div className="text-xs text-slate-500">{patient.subdistrict_name}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {Array.isArray(patient.vulnerable_group_ids) &&
                                patient.vulnerable_group_ids.map((groupId: number) => (
                                  <span
                                    key={groupId}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700"
                                  >
                                    {groupId === 1 && 'ผู้สูงอายุ'}
                                    {groupId === 3 && 'เสี่ยงสูง'}
                                    {groupId === 5 && 'ติดบ้าน/ติดเตียง'}
                                  </span>
                                ))}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-korat-navy-100 text-korat-navy-700">
                              {getTypeLabel(patient.type)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(patient.severity)}`}>
                              {getSeverityLabel(patient.severity)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {patient.chronic_disease_names && patient.chronic_disease_names.length > 0 ? (
                                <>
                                  {patient.chronic_disease_names.slice(0, 3).map((name, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-pink-100 text-pink-700"
                                    >
                                      {name}
                                    </span>
                                  ))}
                                  {patient.chronic_disease_names.length > 3 && (
                                    <span className="text-xs text-slate-500 font-medium">
                                      +{patient.chronic_disease_names.length - 3} เพิ่มเติม
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span className="text-sm text-slate-400">ไม่มีโรคประจำตัว</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => (window.location.hash = `bedridden-patients/${patient.id}/edit`)}
                                className="p-2 text-slate-600 hover:text-korat-navy-700 hover:bg-slate-100 rounded-lg transition-colors"
                                title="แก้ไข"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(patient.id)}
                                className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="ลบ"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
                  <div className="text-sm text-slate-600">
                    แสดง {((pagination.page - 1) * pagination.perPage) + 1} - {Math.min(pagination.page * pagination.perPage, pagination.total)} จาก {pagination.total} รายการ
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="px-3 py-1 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      ก่อนหน้า
                    </button>
                    <span className="px-3 py-1 text-sm text-slate-700">
                      หน้า {pagination.page} / {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page === pagination.totalPages}
                      className="px-3 py-1 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      ถัดไป
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
